import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { AuthUser } from "@codestack/shared";
import { authApi, setAccessToken, setUnauthorizedHandler } from "./api";

type AuthState = {
  user: AuthUser | null;
  status: "loading" | "authenticated" | "anonymous";
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  can: (permission: string) => boolean;
  canAny: (...permissions: string[]) => boolean;
};

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [status, setStatus] = useState<AuthState["status"]>("loading");

  const clear = useCallback(() => {
    setAccessToken(null);
    setUser(null);
    setStatus("anonymous");
  }, []);

  useEffect(() => {
    setUnauthorizedHandler(clear);
    return () => setUnauthorizedHandler(null);
  }, [clear]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const restored = await authApi.bootstrap();
      if (cancelled) return;

      if (!restored) {
        setStatus("anonymous");
        return;
      }

      try {
        const current = await authApi.me();
        if (cancelled) return;
        setUser(current);
        setStatus("authenticated");
      } catch {
        if (!cancelled) clear();
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [clear]);

  const login = useCallback(async (email: string, password: string) => {
    const result = await authApi.login(email, password);
    setUser(result.user);
    setStatus("authenticated");
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } finally {
      clear();
    }
  }, [clear]);

  const refreshUser = useCallback(async () => {
    const current = await authApi.me();
    setUser(current);
  }, []);

  const permissions = useMemo(() => new Set(user?.permissions ?? []), [user]);

  const can = useCallback(
    (permission: string) => permissions.has(permission),
    [permissions],
  );

  const canAny = useCallback(
    (...required: string[]) => required.some((permission) => permissions.has(permission)),
    [permissions],
  );

  const value = useMemo<AuthState>(
    () => ({ user, status, login, logout, refreshUser, can, canAny }),
    [user, status, login, logout, refreshUser, can, canAny],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
